/**
 * VENOA Cookie-Banner – Version 1.0.0 (2026-09-30), snippets/venoa-consent.liquid.
 * Dokumentation und frühere Versionen: consent-dokumentation-2026-09-30/.
 *
 * Eigene Oberfläche, aber keine eigene Einwilligungs-Logik: Gespeichert,
 * geprüft und verteilt wird die Wahl ausschließlich über Shopifys Customer
 * Privacy API. Shopify schreibt sie in seine eigenen Cookies und gibt sie an
 * die Pixel der Vertriebskanäle weiter (Google & YouTube inkl. Consent Mode,
 * Facebook & Instagram, TikTok, Pinterest, eigene Pixel). Das Theme blockiert
 * oder entfernt deshalb nichts selbst.
 *
 * - Erster Besuch (noch keine Wahl bei Shopify): Banner erscheint.
 * - „Akzeptieren“ / „Ablehnen“ setzen analytics, marketing und preferences
 *   gemeinsam auf ja bzw. nein – wie bisher bei Pandectes.
 * - Links auf „#reopenBanner“ (Footer „Cookie-Einstellungen“) öffnen ihn
 *   jederzeit wieder.
 * - Widerruf (vorher ja, jetzt nein): Shopify meldet den Widerruf sofort an
 *   die laufenden Pixel; zusätzlich werden bekannte Mess-Cookies gelöscht.
 *   Bewusst KEIN Neuladen: Im Echttest hat das Neuladen direkt nach dem
 *   Widerruf noch einen Abschieds-Ping von Google ausgelöst, weil der
 *   Widerruf dort noch nicht angekommen war.
 *
 * Die Oberfläche liegt im Shadow DOM (Snippet), damit die in der Seite
 * laufenden Meta-/Google-Pixel Klicks auf die Banner-Buttons nicht als
 * Button-Klick mitzählen.
 */
(() => {
  if (customElements.get('venoa-consent')) return;

  const CATEGORIES = ['analytics', 'marketing', 'preferences'];

  // Eigene Cookies der Mess- und Werbedienste (Google Analytics/Ads, Meta,
  // TikTok, Pinterest, Microsoft). Shopifys eigene Analyse-Cookies räumt
  // Shopify beim Widerruf selbst ab.
  const TRACKING_COOKIE =
    /^(_ga|_ga_[\w-]+|_gid|_gat[\w-]*|_gcl_[\w-]+|_fbp|_fbc|_ttp|_tt_enable_cookie|ttcsid[\w-]*|_pin_unauth|_pinterest_ct_ua|_epik|_uetsid|_uetvid)$/;

  const waitFor = (check, timeout = 10000) =>
    new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        const value = check();
        if (value) return resolve(value);
        if (Date.now() - start > timeout) return reject(new Error('timeout'));
        setTimeout(tick, 100);
      };
      tick();
    });

  const loadCustomerPrivacy = async () => {
    const existing = window.Shopify?.customerPrivacy;
    if (typeof existing?.setTrackingConsent === 'function') return existing;
    const loadFeatures = await waitFor(() => window.Shopify?.loadFeatures);
    await new Promise((resolve, reject) =>
      loadFeatures.call(window.Shopify, [{ name: 'consent-tracking-api', version: '0.1' }], (error) =>
        error ? reject(error) : resolve()
      )
    );
    return waitFor(() => window.Shopify?.customerPrivacy?.setTrackingConsent && window.Shopify.customerPrivacy);
  };

  const clearTrackingCookies = () => {
    const host = window.location.hostname;
    const parts = host.split('.');
    // Host selbst, .host und jede übergeordnete Domain (z. B. .venoaperfume.com)
    const domains = [''];
    for (let i = 0; i < parts.length - 1; i += 1) domains.push(`; domain=.${parts.slice(i).join('.')}`);
    document.cookie
      .split(';')
      .map((entry) => entry.split('=')[0].trim())
      .filter((name) => TRACKING_COOKIE.test(name))
      .forEach((name) => {
        domains.forEach((domain) => {
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
        });
      });
  };

  class VenoaConsent extends HTMLElement {
    connectedCallback() {
      // Deklaratives Shadow DOM; ältere Browser ohne shadowrootmode: selbst anlegen.
      if (!this.shadowRoot) {
        const template = this.querySelector('template');
        if (!template) return;
        this.attachShadow({ mode: 'open' }).append(template.content.cloneNode(true));
        template.remove();
      }
      const root = this.shadowRoot;
      this.panel = root.querySelector('.venoa-consent');
      this.status = root.querySelector('[data-consent-status]');
      this.buttons = [...root.querySelectorAll('[data-consent-accept], [data-consent-deny]')];

      root.querySelector('[data-consent-accept]').addEventListener('click', () => this.choose(true));
      root.querySelector('[data-consent-deny]').addEventListener('click', () => this.choose(false));
      root.querySelector('[data-consent-close]').addEventListener('click', () => this.close());
      this.panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.hasDecision()) this.close();
      });
      document.addEventListener('click', this.onTrigger);

      this.ready = loadCustomerPrivacy().then((privacy) => {
        this.privacy = privacy;
        return privacy;
      });
      this.ready
        .then(() => {
          if (!this.hasDecision()) this.open({ focus: false });
        })
        .catch((error) => console.warn('[VENOA] Cookie-Banner: Shopify Customer Privacy API nicht verfügbar', error));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onTrigger);
    }

    // Links auf „#reopenBanner“ (auch „/#reopenBanner“) und [data-venoa-consent-open]
    onTrigger = (event) => {
      const trigger =
        event.target instanceof Element ? event.target.closest('a[href$="#reopenBanner"], [data-venoa-consent-open]') : null;
      if (!trigger) return;
      event.preventDefault();
      this.returnFocus = trigger;
      this.ready?.then(() => this.open({ focus: true })).catch(() => {});
    };

    consent() {
      return this.privacy?.currentVisitorConsent?.() || {};
    }

    // Eine Wahl liegt vor, sobald Shopify für eine Kategorie „yes“ oder „no“ kennt
    // („“ = noch nie gefragt).
    hasDecision() {
      const consent = this.consent();
      return CATEGORIES.some((key) => consent[key] === 'yes' || consent[key] === 'no');
    }

    isGranted() {
      const consent = this.consent();
      return CATEGORIES.some((key) => consent[key] === 'yes');
    }

    open({ focus }) {
      const decided = this.hasDecision();
      this.toggleAttribute('data-decided', decided);
      this.status.hidden = !decided;
      if (decided) this.status.textContent = this.isGranted() ? this.dataset.statusGranted : this.dataset.statusDenied;
      this.panel.hidden = false;
      this.setAttribute('open', '');
      if (focus) this.panel.focus({ preventScroll: true });
    }

    close() {
      this.panel.hidden = true;
      this.removeAttribute('open');
      if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
      this.returnFocus = null;
    }

    async choose(accepted) {
      if (!this.privacy || this.busy) return;
      const wasGranted = this.isGranted();
      this.busy = true;
      this.buttons.forEach((button) => (button.disabled = true));

      const consent = Object.fromEntries(CATEGORIES.map((key) => [key, accepted]));
      const error = await new Promise((resolve) => {
        const timer = setTimeout(() => resolve(new Error('timeout')), 10000);
        this.privacy.setTrackingConsent(consent, (result) => {
          clearTimeout(timer);
          resolve(result?.error ? new Error(String(result.error)) : null);
        });
      });

      this.busy = false;
      this.buttons.forEach((button) => (button.disabled = false));
      if (error) {
        console.warn('[VENOA] Cookie-Banner: Auswahl konnte nicht gespeichert werden', error);
        return;
      }

      this.close();
      if (!accepted && wasGranted) {
        clearTrackingCookies();
        // Falls ein Pixel noch schreibt, bevor der Widerruf bei ihm ankommt.
        setTimeout(clearTrackingCookies, 3000);
      }
    }
  }

  customElements.define('venoa-consent', VenoaConsent);
})();
